"use client";

import { trackConversion, useAnalytics } from "@analytics";
import { useReportGenerationFlow } from "@marketing/ai-credit-report/hooks/useReportGenerationFlow";
import {
	type PersonReportAttribute,
	generatePersonAiReport,
} from "@marketing/ai-credit-report/lib/api";
import {
	AI_REPORT_EVENTS,
	GTM_CONVERSION_EVENTS,
	MARKETING_EVENTS,
} from "@repo/analytics";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { Label } from "@ui/components/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@ui/components/select";
import {
	AlertCircleIcon,
	CheckCircleIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	PlusIcon,
	Trash2Icon,
	XCircleIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ReportGeneratingPanel } from "./ReportGeneratingPanel";
import { StatusBadge } from "./StatusBadge";

const MAX_ATTRIBUTES = 5;

type Sentiment = PersonReportAttribute["sentiment"];

interface AttributeRow {
	message: string;
	sentiment: Sentiment;
}

export function PersonGenerateForm() {
	const t = useTranslations();
	const [name, setName] = useState("");
	const [entityRef, setEntityRef] = useState("");
	const [attributesOpen, setAttributesOpen] = useState(false);
	const [attributes, setAttributes] = useState<AttributeRow[]>([]);
	const [validationError, setValidationError] = useState<string | null>(null);
	const { setPublicToken, report, isActivelyGenerating, isReportGenerating } =
		useReportGenerationFlow();
	const { trackEvent } = useAnalytics();

	// Track page view when component mounts
	useEffect(() => {
		trackEvent(MARKETING_EVENTS.CTA_CLICKED, {
			location: "personal_ai_visibility_report_page",
			isLoggedIn: false,
		});
	}, [trackEvent]);

	const mutation = useMutation({
		mutationFn: async (input: {
			name: string;
			entityRef?: string;
			attributes?: PersonReportAttribute[];
		}) => {
			// Get PostHog session ID if available
			let sessionId: string | undefined;
			if (typeof window !== "undefined" && (window as any).posthog) {
				sessionId = (window as any).posthog.get_session_id();
			}

			const { report } = await generatePersonAiReport(input, sessionId);
			return report;
		},
		onSuccess: (report) => {
			setValidationError(null);
			setPublicToken(report.publicToken);

			trackEvent(AI_REPORT_EVENTS.REPORT_GENERATION_REQUESTED, {
				domain: name,
				reportId: report.id,
				propertyId: report.id, // Using report ID as property ID for marketing reports
				source: "marketing_page",
			});
			trackConversion(GTM_CONVERSION_EVENTS.GTM_REPORT_PREVIEW_RUN, {
				reportId: report.id,
				subjectType: "person",
			});

			toast.success(
				t("marketing.aiCreditReport.generateForm.status.startedToast"),
				{
					description: t(
						"marketing.aiCreditReport.generateForm.status.startedDescription",
					),
					icon: <CheckCircleIcon className="w-4 h-4" />,
				},
			);
		},
		onError: (error: any) => {
			const errorMessage =
				error?.message ||
				t(
					"marketing.aiCreditReport.generateForm.status.errorDescription",
				);

			setValidationError(errorMessage);

			trackEvent(AI_REPORT_EVENTS.REPORT_GENERATION_FAILED, {
				domain: name,
				reportId: "", // No report ID available on failure
				propertyId: "",
				errorType: "other",
				errorMessage,
			});

			toast.error(
				t("marketing.aiCreditReport.generateForm.status.errorToast"),
				{
					description: errorMessage,
					icon: <XCircleIcon className="w-4 h-4" />,
				},
			);
		},
	});

	// Track pipeline failures surfaced by the poller (the redirect-on-complete
	// lives in useReportGenerationFlow)
	useEffect(() => {
		if (!report) return;

		if (
			report.status === "failed" &&
			report.errorMessage &&
			!validationError
		) {
			trackEvent(AI_REPORT_EVENTS.REPORT_GENERATION_FAILED, {
				domain: name,
				reportId: report.id,
				propertyId: report.id,
				errorType: "other",
				errorMessage: report.errorMessage,
			});
		}
	}, [report, validationError, trackEvent, name]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		const trimmedName = name.trim();
		if (trimmedName.length < 2) {
			setValidationError(
				t("marketing.personAiReport.form.validation.nameRequired"),
			);
			return;
		}

		// Drop empty attribute rows before submit
		const cleanAttributes = attributes
			.map((a) => ({ ...a, message: a.message.trim() }))
			.filter((a) => a.message.length > 0);

		mutation.mutate({
			name: trimmedName,
			entityRef: entityRef.trim() || undefined,
			attributes:
				cleanAttributes.length > 0 ? cleanAttributes : undefined,
		});
	};

	const addAttributeRow = () => {
		setAttributes((rows) =>
			rows.length >= MAX_ATTRIBUTES
				? rows
				: [...rows, { message: "", sentiment: "POSITIVE" }],
		);
	};

	const updateAttributeRow = (
		index: number,
		patch: Partial<AttributeRow>,
	) => {
		setAttributes((rows) =>
			rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
		);
	};

	const removeAttributeRow = (index: number) => {
		setAttributes((rows) => rows.filter((_, i) => i !== index));
	};

	const toggleAttributes = () => {
		setAttributesOpen((open) => {
			const next = !open;
			if (next && attributes.length === 0) {
				setAttributes([{ message: "", sentiment: "POSITIVE" }]);
			}
			return next;
		});
	};

	const hasError = validationError || mutation.isError;
	const isButtonDisabled: boolean = mutation.isPending || isReportGenerating;

	// If a report is actively generating, show the generating panel instead of
	// the form. Person reports have no domain — show the person's name.
	if (isActivelyGenerating && report) {
		return (
			<ReportGeneratingPanel
				publicToken={report.publicToken}
				domain={report.visibilitySummary?.companyName || name}
				status={report.status}
				progressStep={report.progressStep}
				progressPercent={report.progressPercent}
				progressMessage={report.progressMessage}
			/>
		);
	}

	return (
		<div className="space-y-6">
			<form
				className="flex flex-col gap-4 justify-center max-w-2xl mx-auto"
				onSubmit={handleSubmit}
			>
				<div className="space-y-4">
					<div>
						<Label
							htmlFor="person-name"
							className="block text-sm font-medium mb-2"
						>
							{t("marketing.personAiReport.form.nameLabel")}
						</Label>
						<div className="relative">
							<Input
								id="person-name"
								placeholder={t(
									"marketing.personAiReport.form.namePlaceholder",
								)}
								value={name}
								onChange={(e) => {
									setName(e.target.value);
									if (validationError)
										setValidationError(null);
								}}
								maxLength={200}
								data-testid="person-name-input"
								disabled={isButtonDisabled}
								className={`h-12 text-lg px-4 transition-colors ${
									hasError
										? "border-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50/50"
										: mutation.isSuccess
											? "border-green-300 focus:border-green-500 focus:ring-green-500 bg-green-50/50"
											: ""
								}`}
							/>
							{hasError && (
								<AlertCircleIcon
									className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500"
									data-testid="alert-circle-icon"
								/>
							)}
						</div>
						{validationError && (
							<p
								className="mt-1 text-sm text-red-600 flex items-center gap-1"
								data-testid="validation-error"
							>
								<AlertCircleIcon className="w-4 h-4" />
								{validationError}
							</p>
						)}
					</div>

					<div>
						<Label
							htmlFor="person-entity-ref"
							className="block text-sm font-medium text-gray-500 mb-2"
						>
							{t("marketing.personAiReport.form.entityRefLabel")}
						</Label>
						<Input
							id="person-entity-ref"
							placeholder={t(
								"marketing.personAiReport.form.entityRefPlaceholder",
							)}
							value={entityRef}
							onChange={(e) => setEntityRef(e.target.value)}
							maxLength={300}
							data-testid="person-entity-ref-input"
							disabled={isButtonDisabled}
							className="h-10 text-base"
						/>
						<p className="mt-1 text-xs text-gray-500">
							{t("marketing.personAiReport.form.entityRefHelp")}
						</p>
					</div>

					<div>
						<button
							type="button"
							onClick={toggleAttributes}
							disabled={isButtonDisabled}
							className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline disabled:opacity-50"
							data-testid="attributes-toggle"
						>
							{attributesOpen ? (
								<ChevronUpIcon
									className="w-4 h-4"
									aria-hidden
								/>
							) : (
								<ChevronDownIcon
									className="w-4 h-4"
									aria-hidden
								/>
							)}
							{t(
								"marketing.personAiReport.form.attributesToggle",
							)}
						</button>

						{attributesOpen && (
							<div className="mt-3 space-y-3">
								<p className="text-xs text-gray-500">
									{t(
										"marketing.personAiReport.form.attributesHelp",
									)}
								</p>
								{attributes.map((row, index) => (
									<div
										key={index}
										className="flex flex-col sm:flex-row gap-2"
									>
										<Input
											placeholder={t(
												"marketing.personAiReport.form.attributeMessagePlaceholder",
											)}
											value={row.message}
											onChange={(e) =>
												updateAttributeRow(index, {
													message: e.target.value,
												})
											}
											maxLength={200}
											disabled={isButtonDisabled}
											className="flex-1"
											data-testid={`attribute-message-${index}`}
										/>
										<div className="flex gap-2">
											<Select
												value={row.sentiment}
												onValueChange={(v) =>
													updateAttributeRow(index, {
														sentiment:
															v as Sentiment,
													})
												}
												disabled={isButtonDisabled}
											>
												<SelectTrigger
													className="w-36"
													data-testid={`attribute-sentiment-${index}`}
												>
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="POSITIVE">
														{t(
															"marketing.personAiReport.form.sentiment.positive",
														)}
													</SelectItem>
													<SelectItem value="NEGATIVE">
														{t(
															"marketing.personAiReport.form.sentiment.negative",
														)}
													</SelectItem>
													<SelectItem value="NEUTRAL">
														{t(
															"marketing.personAiReport.form.sentiment.neutral",
														)}
													</SelectItem>
												</SelectContent>
											</Select>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												onClick={() =>
													removeAttributeRow(index)
												}
												disabled={isButtonDisabled}
												aria-label={t(
													"marketing.personAiReport.form.removeRow",
												)}
											>
												<Trash2Icon className="w-4 h-4 text-muted-foreground" />
											</Button>
										</div>
									</div>
								))}
								{attributes.length < MAX_ATTRIBUTES && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={addAttributeRow}
										disabled={isButtonDisabled}
										data-testid="add-attribute-row"
									>
										<PlusIcon
											className="w-4 h-4 mr-1.5"
											aria-hidden
										/>
										{t(
											"marketing.personAiReport.form.addRow",
										)}
									</Button>
								)}
							</div>
						)}
					</div>

					<Button
						type="submit"
						variant="primary"
						disabled={isButtonDisabled}
						size="lg"
						className="h-12 px-8 text-lg font-medium w-full sm:w-auto"
						data-testid="generate-person-button"
					>
						{isButtonDisabled &&
						(mutation.isPending || isReportGenerating)
							? t("marketing.aiCreditReport.generatingText")
							: t("marketing.personAiReport.form.submit")}
					</Button>
				</div>
			</form>

			{report && report.status === "failed" && (
				<StatusBadge
					type="failed"
					message={
						report.errorMessage ||
						t("marketing.aiCreditReport.generateForm.status.failed")
					}
					testId="status-failed"
				/>
			)}

			{report && report.status === "cancelled" && (
				<StatusBadge
					type="failed"
					message={
						report.errorMessage ||
						t(
							"marketing.aiCreditReport.generateForm.status.cancelled",
						)
					}
					testId="status-cancelled"
				/>
			)}
		</div>
	);
}
